import { LineString } from "geojson"
import { FlightlineEvent, FlightlineEventDTO } from "../models/FlightlineEvent"
import { SortOrder } from "../models/SortOrder"
import { CreatedFlightPlannedLine } from "../store/types/FlightPlansTypes"
import { getComparator } from "./TableUtils"

export const sortFlightlineEvents = (events: FlightlineEvent[], sortOrder: SortOrder<keyof FlightlineEvent>) => {
    const { order, orderBy } = sortOrder
    return events.slice().sort(getComparator(order, orderBy))
}

const template = "00000"
export const parseFlightlineEventNames = (
    flightlineEvents: FlightlineEvent[] | FlightlineEventDTO[],
    matchingExpression: string,
    eventPlannedLines?: EventPlannedLine[],
): FlightlineEvent[] => {
    return flightlineEvents.map((event, index): FlightlineEvent => {
        const { line_original_name } = event

        let lineNameFromOriginal
        try {
            const matches = new RegExp(matchingExpression ?? "").exec(line_original_name)
            if (matches && matches[1] != null) {
                const capturedLineName = matches[1]
                if (capturedLineName.length <= template.length) {
                    const paddedLineName =
                        template.slice(0, template.length - capturedLineName.length) + capturedLineName

                    // Some file formats use a short, zero-padded planned-line name
                    // directly (for example, Leica uses "001"), while the existing
                    // formats commonly need the captured number padded to five digits.
                    // Prefer the exact captured name when it exists in the selected
                    // flight plan, then fall back to the established padding behavior.
                    const exactMatch = eventPlannedLines?.some(
                        (plannedLine) => plannedLine.plannedLineName === capturedLineName,
                    )
                    lineNameFromOriginal = exactMatch ? capturedLineName : paddedLineName
                }
            }
        } catch {
            // fields use the null default
        }

        const firstMatchingPlannedLineId: number[] = []
        if (lineNameFromOriginal && eventPlannedLines) {
            const match = findFirstMatchingEventPlannedLine(lineNameFromOriginal, eventPlannedLines)
            match && firstMatchingPlannedLineId.push(match)
        }

        return {
            ...event,
            fe_event_id: index + 1,
            fe_planned_line_ids: firstMatchingPlannedLineId,
            fe_name_from_original: lineNameFromOriginal,
        }
    })
}

export const findFirstMatchingEventPlannedLine = (
    name: string,
    eventPlannedLines: EventPlannedLine[],
): number | undefined => {
    const eventPlannedLine = eventPlannedLines.find((pl) => pl.plannedLineName === name)
    if (eventPlannedLine) {
        return eventPlannedLine.plannedLineId
    }
}

export interface EventPlannedLine {
    label: string
    plannedLineId: number
    flightPlanId: number
    plannedLineName: string
    frameCount?: number
    shape?: LineString
}

export const unplannedLineLabel = "UL"
export const unplannedLineValue = -1
const unplannedLineOption: EventPlannedLine = {
    label: unplannedLineLabel,
    plannedLineId: unplannedLineValue,
    flightPlanId: unplannedLineValue,
    plannedLineName: unplannedLineLabel,
}

export const getEventPlannedLines = (plannedLines: CreatedFlightPlannedLine[]): EventPlannedLine[] => {
    const result: EventPlannedLine[] = plannedLines.map((plannedLine): EventPlannedLine => {
        return {
            label: `${plannedLine.flight_plan_id} - ${plannedLine.flight_line_name}`,
            plannedLineId: plannedLine.planned_line_id,
            flightPlanId: plannedLine.flight_plan_id,
            plannedLineName: plannedLine.flight_line_name,
            frameCount: plannedLine.frame_count,
            shape: plannedLine.shape,
        }
    })
    result.unshift(unplannedLineOption)
    return result
}

export const isAutocompletePlannedLineOptionDisabled = (
    opt: EventPlannedLine,
    selectedPlannedLineIds: number[],
    selectedFlightPlan?: number,
): boolean => {
    // unplannedLine selected -> disable everything else
    const unplannedSelected =
        selectedPlannedLineIds.includes(unplannedLineValue) && opt.plannedLineId !== unplannedLineValue

    // Disable unplanned line option
    const plannedLineSelected =
        selectedPlannedLineIds.length > 0 &&
        !selectedPlannedLineIds.includes(unplannedLineValue) &&
        opt.plannedLineId === unplannedLineValue

    // Planned lines are selected = disable lines from other flight plans
    const notMatchingFlightPlan = !!selectedFlightPlan && opt.flightPlanId !== selectedFlightPlan
    return unplannedSelected || plannedLineSelected || notMatchingFlightPlan
}
